import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Box,
  Typography
} from '@mui/material';
import VeersaLoaderGif from "../../common/icons/Veersa-loader.gif";
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { createTask } from '../../http/requests/task';
import { getAllUsers } from '../../http/requests/dropdown';
import { getEmployeeDropdownData } from '../../http/requests/session';
import { getFullName } from '../../common/utils/datatable';
import { toastOptions } from '../../common/utils/toasterOptions';
import './TaskCreationModal.css';

const TaskCreationModal = ({ open, onClose, selectedBenchEmployee = null, selectedEmployee = null, onTaskCreated = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedToUserID: ''
  });
  const [employeeDropdownData, setEmployeeDropdownData] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployeeForAssignment, setSelectedEmployeeForAssignment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { allUserDetails } = useSelector(({ MODULES }) => MODULES);
  const dispatch = useDispatch();
  
  // Use either prop name for backward compatibility
  const selectedBenchEmployeeData = selectedBenchEmployee || selectedEmployee;
  // Fetch employees on component mount
  useEffect(() => {
    console.log('TaskCreationModal useEffect triggered:', { open, selectedBenchEmployeeData });
    if (open) {
      fetchEmployees();
      // Pre-populate form data when modal opens with selected employee
      if (selectedBenchEmployeeData) {
        console.log('Pre-populating form with employee:', selectedBenchEmployeeData);
        setFormData({
          title: `Bench Allocation Fix for ${selectedBenchEmployeeData.name}`,
          description: `Please fix allocation for ${selectedBenchEmployeeData.name} (ID: ${selectedBenchEmployeeData.employeeId}).`,
          assignedToUserID: ''
        });
      } else {
        console.log('No selectedBenchEmployeeData found, resetting form');
        // Reset form when no employee is selected
        setFormData({
          title: '',
          description: '',
          assignedToUserID: ''
        });
      }
    }
  }, [open, selectedBenchEmployeeData]);

  // Filter employees based on search term - same logic as regularize attendance
  useEffect(() => {
    const sourceData = employeeDropdownData.length > 0 ? employeeDropdownData : (allUserDetails?.data || []);
    
    if (!searchTerm) {
      setFilteredEmployees(sourceData);
    } else {
      const filtered = sourceData.filter((employee) => {
        // Handle both employee dropdown format and allUserDetails format
        const name = (employee.name || getFullName(employee.firstName, employee.lastName) || '').toLowerCase();
        const employeeId = (employee.employeeId || '').toLowerCase();
        const email = (employee.email || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        
        return name.includes(search) || employeeId.includes(search) || email.includes(search);
      });
      setFilteredEmployees(filtered);
    }
  }, [employeeDropdownData, allUserDetails?.data, searchTerm]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await getEmployeeDropdownData();
      if (response) {
        setEmployeeDropdownData(response);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees', toastOptions);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Task title is required', toastOptions);
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Task description is required', toastOptions);
      return;
    }
    
    if (!selectedEmployeeForAssignment) {
      toast.error('Please select an assignee', toastOptions);
      return;
    }

    try {
      setSubmitting(true);
      
      const taskData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        assignedToUserID: selectedEmployeeForAssignment.azureId || selectedEmployeeForAssignment.id,
        projectAllocationId: selectedBenchEmployeeData?.projectAllocationId || 0
      };

      console.log('Creating task with data:', taskData);
      console.log('Selected bench employee data:', selectedBenchEmployeeData);
      console.log('Project Allocation ID being sent:', taskData.projectAllocationId);
      
      const response = await createTask(taskData);
      
      if (response) {
        toast.success('Task created successfully!', toastOptions);
        
        // Call the callback to update task counts
        if (onTaskCreated) {
          console.log('🔄 Calling onTaskCreated callback...');
          await onTaskCreated({
            taskData,
            response,
            employeeData: selectedBenchEmployeeData
          });
        }
        
        handleClose();
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task. Please try again.', toastOptions);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      title: '',
      description: '',
      assignedToUserID: ''
    });
    setSelectedEmployeeForAssignment(null);
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ 
          fontWeight: 'bold', 
          color: '#1773bc',
          textAlign: 'center',
          marginBottom: '8px'
        }}>
          📝 Create New Task
        </Typography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ padding: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Task Title */}
            <TextField
              fullWidth
              label="Task Title"
              variant="outlined"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter a clear and concise task title"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />

            {/* Description */}
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Provide detailed information about the task, requirements, and expectations"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />

            {/* Assign To Dropdown - Same as regularize attendance */}
            <Autocomplete
              fullWidth
              options={filteredEmployees}
              getOptionLabel={(option) => {
                if (!option) return '';
                // Handle both employee dropdown format and allUserDetails format
                const name = option.name || getFullName(option.firstName, option.lastName);
                const employeeId = option.employeeId || '';
                // Display format: "Name (EmployeeID)" similar to session nomination
                return employeeId ? `${name} (${employeeId})` : name;
              }}
              value={selectedEmployeeForAssignment}
              onChange={(event, newValue) => {
                console.log('Employee selected:', newValue);
                setSelectedEmployeeForAssignment(newValue);
              }}
              onInputChange={(event, newInputValue, reason) => {
                console.log('Input change - value:', newInputValue, 'reason:', reason);
                if (reason === 'input') {
                  setSearchTerm(newInputValue);
                } else if (reason === 'clear') {
                  setSearchTerm('');
                  setSelectedEmployeeForAssignment(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign To"
                  placeholder="Type to search employees by name, ID, or email..."
                  helperText="Start typing to filter employees"
                  variant="outlined"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px'
                    },
                    '& .MuiInputBase-root': { 
                      minHeight: '56px' 
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <div style={{ width: '100%' }}>
                    <div style={{ fontWeight: 500 }}>
                      {option.name || getFullName(option.firstName, option.lastName)} 
                      {option.employeeId && (
                        <span style={{ color: '#666', fontWeight: 400 }}>
                          {' '}({option.employeeId})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#666' }}>
                      {option.email}
                    </div>
                  </div>
                </li>
              )}
              ListboxProps={{
                sx: { maxHeight: 300 }
              }}
              noOptionsText={searchTerm ? "No employees found matching your search" : "Start typing to search employees"}
              isOptionEqualToValue={(option, value) => {
                // Handle both data formats for comparison
                const optionId = option?.azureId || option?.id;
                const valueId = value?.azureId || value?.id;
                return optionId === valueId;
              }}
              freeSolo={false}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
            size="large"
            sx={{
              borderRadius: '8px',
              color: '#666',
              borderColor: '#999',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#666'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            size="large"
            sx={{
              borderRadius: '8px',
              backgroundColor: '#1773bc',
              '&:hover': {
                backgroundColor: '#125a94'
              },
              minWidth: '120px'
            }}
          >
            {submitting ? (
              <>
                <div style={{
                  backgroundColor: 'white',
                  padding: '2px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src={VeersaLoaderGif} 
                    alt="Loading..." 
                    style={{ width: '26px', height: '26px' }}
                  />
                </div>
                Creating...
              </>
            ) : (
              'Create Task'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TaskCreationModal;
